import { useState } from "react";
import { isAxiosError } from "axios";
import { Download, Info, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useImportExportUrl } from "@/hooks/use-itsm-import";
import { openDownload } from "./export-csv";
import { ACTIVE_STATUSES } from "@/lib/types/itsm-import";
import type { ImportJobResponse } from "@/lib/types/itsm-import";

interface ExportCsvSectionProps {
  job: ImportJobResponse;
  tenantId: string;
}

export function ExportCsvSection({ job, tenantId }: ExportCsvSectionProps) {
  const exportUrl = useImportExportUrl(tenantId);
  const [noFile, setNoFile] = useState(false);
  const isActive = ACTIVE_STATUSES.includes(job.status);
  const isCompleted = job.status === "completed";

  const handleDownload = async () => {
    try {
      const { download_url } = await exportUrl.mutateAsync(job.id);
      openDownload(download_url);
    } catch (err: unknown) {
      // The job is an export, so a 404 means it wrote no file.
      if (isAxiosError(err) && err.response?.status === 404) {
        setNoFile(true);
        return;
      }
      if (isAxiosError(err) && err.response?.status === 409) {
        toast.error("The export is not ready yet");
        return;
      }
      toast.error("Failed to get the CSV download link");
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">
          CSV Export
        </h3>
        <Button
          size="sm"
          onClick={() => { void handleDownload(); }}
          disabled={!isCompleted || noFile || exportUrl.isPending}
        >
          {exportUrl.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          Download CSV
        </Button>
      </div>
      {noFile && (
        <div className="flex items-start gap-3 rounded-lg border p-4 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <p>This export found no tickets, so there is no CSV file to download.</p>
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold">{job.fetched_tickets}</div>
            <div className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
              {job.status === "fetching" && <Loader2 className="h-3 w-3 animate-spin" />}
              Fetched
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold">{job.succeeded_tickets}</div>
            <div className="text-sm text-muted-foreground">Exported</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <div className={`text-3xl font-bold ${job.failed_tickets_count > 0 ? "text-destructive" : ""}`}>
              {job.failed_tickets_count}
            </div>
            <div className="text-sm text-muted-foreground">Failed</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold">{job.processed_tickets}</div>
            <div className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
              {isActive && <Loader2 className="h-3 w-3 animate-spin" />}
              Processed
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
